// Quality of Service (Domain 04): MQC classification, marking and LLQ/CBWFQ
// queueing on congested WAN interfaces.
//
// A lab may describe its user traffic as steady flows:
//   net.traffic = [{ id, name, src, dst, proto, port, app, kbps, dscp, type }]
//     src     host id that sends the flow           dst  destination IP
//     app     NBAR-style application name ('rtp', 'https', 'smb', ...)
//     dscp    marking set by the endpoint (0 = best effort)
//     type    'voice' | 'video' | 'data' | 'bulk'  (only used to grade quality)
// Each flow follows the real forwarding path (sim.forward). On every router it
// meets input policies (classification + `set dscp`) and output policies
// (classification + priority / bandwidth queues). Where the offered load on an
// egress interface exceeds its line rate, the queueing rules below decide what
// is delivered and what is dropped, and how much queueing delay/jitter a class
// sees. Show commands (show policy-map interface, show interfaces) and the
// users' experience are both derived from this one result.
//
// It is a fluid (rate-based) model, not a packet scheduler: enough to show why
// voice breaks during congestion and how LLQ fixes it, without pretending to be
// a full QoS implementation (no shaping, policing actions, WRED or hierarchical
// policies).

import { clone } from './model.js';
import { signature, ifBandwidth } from './routing.js';
import { forward, hostIp, linkOf, isHost } from './sim.js';

export const DSCP = { default: 0, cs1: 8, af11: 10, af12: 12, af13: 14, cs2: 16, af21: 18, af22: 20, af23: 22, cs3: 24, af31: 26, af32: 28, af33: 30, cs4: 32, af41: 34, af42: 36, af43: 38, cs5: 40, ef: 46, cs6: 48, cs7: 56 };
export const dscpValue = (v) => (v == null ? null : /^\d+$/.test(String(v)) ? (+v <= 63 ? +v : null) : DSCP[String(v).toLowerCase()] ?? null);
export const dscpName = (n) => Object.keys(DSCP).find((k) => DSCP[k] === n) || String(n);
export const APPS = ['rtp', 'sip', 'http', 'https', 'smb', 'ftp', 'dns', 'icmp'];

/** Physical line rate (kbps) — what actually limits throughput. `bandwidth` is only informational. */
export function lineRate(net, id, ifc) {
  const l = linkOf(net, id, ifc);
  if (l?.cable?.rateKbps) return l.cable.rateKbps;
  if (/^Serial/.test(ifc)) return 1544;
  if (/^FastEthernet/.test(ifc)) return 100000;
  if (/^Tunnel/.test(ifc)) return Infinity; // the underlay interface is where congestion happens
  return 1000000;
}

const qosOf = (d) => d?.qos || null;
export function classMapMatches(cm, flow, dscp) {
  if (!cm) return false;
  const tests = (cm.matches || []).map((m) => {
    if (m.type === 'any') return true;
    if (m.type === 'dscp') return m.values.some((v) => dscpValue(v) === dscp);
    if (m.type === 'protocol') return m.value === flow.app;
    return false;
  });
  if (!tests.length) return false;
  return cm.matchAny ? tests.some(Boolean) : tests.every(Boolean);
}
/** First class of the policy that matches (class-default otherwise). */
export function classify(d, policyName, flow, dscp) {
  const pm = qosOf(d)?.policyMaps?.[policyName];
  if (!pm) return null;
  for (const c of pm.classes) {
    if (c.name === 'class-default') continue;
    if (classMapMatches(qosOf(d).classMaps?.[c.name], flow, dscp)) return c;
  }
  return pm.classes.find((c) => c.name === 'class-default') || { name: 'class-default' };
}
const kbpsOf = (spec, bw) => (!spec ? 0 : spec.kbps != null ? spec.kbps : Math.floor((bw * spec.pct) / 100));

/**
 * Allocate a congested interface. classes: [{ name, offered, priority, bandwidth }]
 * Returns { served:{name:kbps}, congested }
 */
export function allocate(cap, classes) {
  const total = classes.reduce((a, c) => a + c.offered, 0);
  const served = {};
  if (total <= cap) { for (const c of classes) served[c.name] = c.offered; return { served, congested: false }; }
  let rem = cap;
  for (const c of classes.filter((x) => x.priority)) { served[c.name] = Math.min(c.offered, c.priority); rem -= served[c.name]; }
  rem = Math.max(0, rem);
  const others = classes.filter((x) => !x.priority);
  const guar = others.map((c) => Math.min(c.offered, c.bandwidth || 0));
  const gsum = guar.reduce((a, b) => a + b, 0);
  const scale = gsum > rem ? rem / gsum : 1;
  others.forEach((c, k) => { served[c.name] = guar[k] * scale; });
  let left = rem - gsum * scale;
  const demand = others.map((c) => c.offered - served[c.name]);
  const dsum = demand.reduce((a, b) => a + b, 0);
  if (dsum > 0 && left > 0) others.forEach((c, k) => { served[c.name] += Math.min(demand[k], (left * demand[k]) / dsum); });
  return { served, congested: true };
}

const memo = new WeakMap();
/** Full QoS picture for the network (memoised per configuration). */
export function qosAnalyze(net) {
  const flows = net.traffic || [];
  const sig = signature(net);
  const m = memo.get(net);
  if (m && m.sig === sig) return m.res;
  const res = { ifs: {}, inputs: {}, flows: {} };
  const egress = {}; // key -> [{ flow, kbps, cls, dscp }]
  const routes = {};
  // 1) path + marking + classification of every flow
  for (const f of flows) {
    const n = clone(net);
    const s = n.devices[f.src];
    const src = s && isHost(s) ? hostIp(s)?.ip : null;
    const fw = src ? forward(n, f.src, { src, dst: f.dst, proto: f.proto || 'udp', dport: f.port || 0, flow: 7e6 + flows.indexOf(f) }) : { ok: false, path: [] };
    let dscp = f.dscp || 0;
    const hops = [];
    for (const hop of fw.path || []) {
      const d = net.devices[hop.dev];
      if (!d || d.kind !== 'router') continue;
      const inPol = hop.in ? d.interfaces[hop.in]?.servicePolicy?.input : null;
      if (inPol && qosOf(d)?.policyMaps?.[inPol]) {
        const c = classify(d, inPol, f, dscp);
        const k = `${hop.dev}|${hop.in}`;
        const rec = (res.inputs[k] ||= { dev: hop.dev, ifc: hop.in, policy: inPol, classes: {} });
        const cr = (rec.classes[c.name] ||= { offered: 0, flows: [] });
        cr.offered += f.kbps; cr.flows.push(f.id);
        if (c.set?.dscp != null) { dscp = dscpValue(c.set.dscp); cr.setDscp = c.set.dscp; }
      }
      const outPol = d.interfaces[hop.out]?.servicePolicy?.output;
      let cls = null;
      if (outPol && qosOf(d)?.policyMaps?.[outPol]) {
        cls = classify(d, outPol, f, dscp);
      }
      const k = `${hop.dev}|${hop.out}`;
      (egress[k] ||= []).push({ flow: f, kbps: f.kbps, cls, dscp, policy: outPol && qosOf(d)?.policyMaps?.[outPol] ? outPol : null });
      hops.push({ key: k, cls: cls?.name || null });
      if (cls?.set?.dscp != null) dscp = dscpValue(cls.set.dscp);
    }
    routes[f.id] = { ok: !!fw.ok, hops, dscp };
  }
  // 2) queueing on every egress interface
  for (const [k, list] of Object.entries(egress)) {
    const [dev, ifc] = k.split('|');
    const d = net.devices[dev];
    const cap = lineRate(net, dev, ifc);
    const bw = ifBandwidth(ifc, d.interfaces[ifc]);
    const policy = list[0].policy;
    const pm = policy ? qosOf(d).policyMaps[policy] : null;
    const byClass = {};
    for (const e of list) {
      const name = policy ? e.cls.name : 'fifo';
      const c = (byClass[name] ||= { name, offered: 0, flows: [], conf: pm?.classes.find((x) => x.name === name) || null });
      c.offered += e.kbps; c.flows.push(e.flow.id);
    }
    const classes = Object.values(byClass).map((c) => ({ ...c, priority: kbpsOf(c.conf?.priority, bw), bandwidth: kbpsOf(c.conf?.bandwidth, bw) }));
    const a = allocate(cap, classes);
    const out = { dev, ifc, cap, bw, policy, congested: a.congested, offered: 0, served: 0, classes: {} };
    for (const c of classes) {
      const served = a.served[c.name];
      const prio = !!c.priority;
      out.classes[c.name] = {
        offered: c.offered, served, drop: Math.max(0, c.offered - served), priority: c.priority || 0, bandwidth: c.bandwidth || 0,
        delay: !a.congested ? 1 : prio ? 2 : policy ? 70 : 110, jitter: !a.congested ? 1 : prio ? 2 : policy ? 30 : 45, flows: c.flows,
      };
      out.offered += c.offered; out.served += served;
    }
    // classes configured in the policy but with no traffic still show up (0 bps)
    if (pm) for (const c of pm.classes) if (!out.classes[c.name]) out.classes[c.name] = { offered: 0, served: 0, drop: 0, priority: kbpsOf(c.priority, bw), bandwidth: kbpsOf(c.bandwidth, bw), delay: 1, jitter: 1, flows: [] };
    res.ifs[k] = out;
  }
  // 3) per-flow user experience
  for (const f of flows) {
    const r = routes[f.id];
    if (!r.ok) { res.flows[f.id] = { reachable: false, loss: 100, delay: null, jitter: null, quality: 'Down', mos: null }; continue; }
    let frac = 1; let delay = 2; let jitter = 0;
    for (const h of r.hops) {
      const st = res.ifs[h.key];
      const c = st.classes[st.policy ? h.cls : 'fifo'];
      frac *= c.offered ? c.served / c.offered : 1;
      delay += c.delay + (/\|Serial/.test(h.key) ? 9 : 1);
      jitter += c.jitter;
    }
    const loss = +(100 * (1 - frac)).toFixed(1);
    const mos = Math.max(1, Math.min(4.4, 4.4 - loss * 0.15 - Math.max(0, jitter - 20) * 0.025 - Math.max(0, delay - 150) * 0.01));
    const sensitive = f.type === 'voice' || f.type === 'video';
    const quality = sensitive
      ? (mos >= 4.0 ? 'Good' : mos >= 3.6 ? 'Fair' : 'Poor')
      : f.type === 'bulk' ? (loss < 1 ? 'Full speed' : 'Throttled by congestion')
        : (loss < 1 ? 'Good' : loss < 10 ? 'Slow' : 'Poor');
    res.flows[f.id] = { reachable: true, loss, delay: Math.round(delay), jitter: Math.round(jitter), mos: +mos.toFixed(1), quality, dscp: r.dscp, path: r.hops.map((h) => h.key) };
  }
  memo.set(net, { sig, res });
  return res;
}

/** Load figures for `show interfaces` (null when the lab has no traffic model). */
export function ifLoad(net, id, ifc) {
  if (!net.traffic?.length) return null;
  const q = qosAnalyze(net);
  const tx = q.ifs[`${id}|${ifc}`];
  let rx = 0;
  for (const st of Object.values(q.ifs)) {
    const l = linkOf(net, st.dev, st.ifc);
    if (!l) continue;
    const far = l.a.dev === st.dev && l.a.ifc === st.ifc ? l.b : l.a;
    if (far.dev === id && far.ifc === ifc) rx += st.served;
  }
  const cap = lineRate(net, id, ifc);
  const txK = tx ? tx.served : 0;
  return {
    txKbps: txK, rxKbps: rx, offered: tx ? tx.offered : 0, drops: tx ? Object.values(tx.classes).reduce((a, c) => a + c.drop, 0) : 0,
    txload: Math.min(255, Math.max(1, Math.round((255 * txK) / cap))), rxload: Math.min(255, Math.max(1, Math.round((255 * rx) / cap))), congested: !!tx?.congested, policy: tx?.policy || null,
  };
}
/** Deterministic packet counts for a 5-minute window at a given rate. */
export const pktsFor = (kbps, size = 1000) => Math.round((kbps * 1000 * 300) / 8 / size);
